import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { Share2, Copy, MessageCircle, Mail, Facebook } from 'lucide-react';
import { toast } from 'sonner';

const APP_URL = 'https://soumissionpro.net';

/* ------------------------------------------------------------------ */
/*  MESSAGES OPTIMISÉS PAR CANAL                                       */
/*  Chaque canal a son ton et sa longueur idéale.                      */
/* ------------------------------------------------------------------ */
function buildMessages(lang) {
  if (lang === 'fr') {
    return {
      // SMS — court, percutant, max ~160 caractères
      sms:
        `Salut ! Découvre SoumissionPro 🇨🇦 : faire une soumission de ` +
        `construction en 10 min depuis ton cell, signature client à ` +
        `distance, 1500+ matériaux pré-chargés. ${APP_URL}`,

      // Courriel — chaleureux, structuré, signature
      emailSubject:
        'SoumissionPro — l\'outil qui change la vie des entrepreneurs canadiens',
      emailBody:
        `Salut,\n\n` +
        `Je voulais te parler d'une app que j'utilise et qui m'a fait gagner ` +
        `un temps fou : SoumissionPro.\n\n` +
        `C'est conçu au Canada, pensé pour le terrain, et ça fait ` +
        `littéralement tout ce qu'un entrepreneur a besoin :\n\n` +
        `  •  Soumissions complètes en moins de 10 minutes depuis le téléphone\n` +
        `  •  Catalogue de 1 500+ matériaux pré-chargés, prix ajustés par IA\n` +
        `  •  Signature numérique sur place OU à distance par courriel\n` +
        `  •  PDF premium avec ton logo et badge licence vérifiée\n` +
        `  •  Fonctionne 100 % hors-ligne (parfait pour les chantiers en région)\n` +
        `  •  Photos avant/après et suivi des dépenses par projet\n` +
        `  •  Calcul automatique TPS, TVQ, TVH et PST partout au Canada\n\n` +
        `Ça remplace Excel, les soumissions papier, et les apps américaines ` +
        `mal traduites. Vraiment fait pour nous autres.\n\n` +
        `À partir de 29,99 $/mois (ou 265 $/année).\n\n` +
        `Essaie-la ici 👉  ${APP_URL}\n\n` +
        `Tu vas l'adorer.\n` +
        `\n` +
        `— Envoyé via SoumissionPro`,

      // Facebook — accrocheur, emojis, social
      facebookQuote:
        `🍷 SoumissionPro — l'outil de soumissions des entrepreneurs ` +
        `canadiens.\n\n` +
        `✔️ Soumissions complètes en 10 minutes depuis ton cell\n` +
        `✔️ 1 500+ matériaux pré-chargés, prix ajustés par IA\n` +
        `✔️ Signature à distance par courriel\n` +
        `✔️ Fonctionne hors-ligne sur le chantier\n` +
        `✔️ PDF premium + badge licence vérifiée\n\n` +
        `Conçu au Canada. À partir de 29,99 $/mois.\n\n` +
        `👉 soumissionpro.net`,

      // Web Share API natif (système d'exploitation)
      nativeText:
        `SoumissionPro — soumissions de construction en 10 min depuis ton cell. ` +
        `1 500+ matériaux, signature à distance, hors-ligne. Conçu au Canada. ` +
        `${APP_URL}`,
    };
  }
  // English
  return {
    sms:
      `Hey! Check out SoumissionPro 🇨🇦: construction quotes in 10 min ` +
      `from your phone, remote client signing, 1500+ materials built-in. ` +
      `${APP_URL}`,
    emailSubject: 'SoumissionPro — the quoting tool every contractor needs',
    emailBody:
      `Hi,\n\n` +
      `Wanted to share an app I use that's saved me a ton of time: ` +
      `SoumissionPro.\n\n` +
      `Built in Canada, made for the field, it does literally everything a ` +
      `contractor needs:\n\n` +
      `  •  Full quotes in under 10 minutes from your phone\n` +
      `  •  1,500+ pre-loaded materials with AI-adjusted pricing\n` +
      `  •  Digital signature on-site OR remotely by email\n` +
      `  •  Premium PDFs with your logo and verified licence badge\n` +
      `  •  Works 100% offline (perfect for remote job sites)\n` +
      `  •  Photo gallery and per-project expense tracking\n` +
      `  •  Automatic GST, QST, HST and PST across all Canadian provinces\n\n` +
      `Starting at $29.99/month (or $265/year).\n\n` +
      `Try it here 👉  ${APP_URL}\n\n` +
      `You'll love it.\n\n` +
      `— Sent via SoumissionPro`,
    facebookQuote:
      `🍷 SoumissionPro — the quoting tool built for contractors.\n\n` +
      `✔️ Full quotes in 10 minutes from your phone\n` +
      `✔️ 1,500+ pre-loaded materials, AI-adjusted prices\n` +
      `✔️ Remote signing by email\n` +
      `✔️ Works fully offline on the job site\n` +
      `✔️ Premium PDFs + verified licence badge\n\n` +
      `Built in Canada. From $29.99/month.\n\n` +
      `👉 soumissionpro.net`,
    nativeText:
      `SoumissionPro — construction quotes in 10 min from your phone. ` +
      `1,500+ materials, remote signing, offline mode. Built in Canada. ` +
      `${APP_URL}`,
  };
}

export function ShareAppCard() {
  const { lang } = useT();
  const [copied, setCopied] = useState(false);
  const msg = buildMessages(lang);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
      setCopied(true);
      toast.success(lang === 'fr' ? '✓ Lien copié' : '✓ Link copied', { duration: 1500 });
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error(lang === 'fr' ? 'Échec de la copie' : 'Copy failed');
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'SoumissionPro',
          text: msg.nativeText,
          url: APP_URL,
        });
      } catch (e) {
        // navigator.share throws AbortError when user cancels the share sheet
        // — this is expected behaviour, no action needed.
        if (e?.name !== 'AbortError') console.warn('[share] native share failed:', e);
      }
    } else {
      copyLink();
    }
  };

  const shareSMS = () => {
    // Use `&` separator for cross-platform compatibility (iOS prefers `&`)
    const url = `sms:?&body=${encodeURIComponent(msg.sms)}`;
    window.location.href = url;
  };

  const shareEmail = () => {
    const url = `mailto:?subject=${encodeURIComponent(msg.emailSubject)}&body=${encodeURIComponent(msg.emailBody)}`;
    window.location.href = url;
  };

  const shareFacebook = () => {
    // Facebook 'sharer' picks up Open Graph tags from APP_URL; we also pass a quote.
    const url =
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(APP_URL)}` +
      `&quote=${encodeURIComponent(msg.facebookQuote)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  return (
    <div
      data-testid="share-app-card"
      className="card p-4 border-2 border-[#C9A961]"
      style={{ background: 'linear-gradient(135deg, #FAF7F2 0%, #fff 100%)' }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center shrink-0">
          <Share2 size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-base text-[#7A1F2B]">
            {lang === 'fr' ? 'Partager SoumissionPro' : 'Share SoumissionPro'}
          </div>
          <div className="text-xs text-gray-600 mt-1 leading-relaxed">
            {lang === 'fr'
              ? 'Faites découvrir l\'app à un autre entrepreneur — chaque canal envoie un message déjà rédigé, professionnel et prêt à partir.'
              : 'Tell another contractor about the app — each channel sends a pre-written, polished message ready to go.'}
          </div>

          <div className="mt-3 p-2.5 rounded-lg bg-white border border-[#E5DCC9] flex items-center gap-2">
            <span className="text-xs text-gray-700 font-mono truncate flex-1">{APP_URL}</span>
            <button
              data-testid="share-copy-link"
              onClick={copyLink}
              className="text-[11px] font-bold px-2 py-1 rounded bg-[#7A1F2B] text-white shrink-0"
              aria-label="Copy link"
            >
              {copied ? '✓' : <Copy size={12} />}
            </button>
          </div>

          <button
            data-testid="share-native-btn"
            onClick={shareNative}
            className="btn-primary w-full gap-2 mt-3 !min-h-[44px] !text-sm"
          >
            <Share2 size={14} /> {lang === 'fr' ? 'Partager' : 'Share'}
          </button>

          <div className="grid grid-cols-3 gap-2 mt-2">
            <button
              data-testid="share-sms-btn"
              onClick={shareSMS}
              className="btn-outline gap-1 !min-h-[40px] !text-[11px]"
            >
              <MessageCircle size={13} /> SMS
            </button>
            <button
              data-testid="share-email-btn"
              onClick={shareEmail}
              className="btn-outline gap-1 !min-h-[40px] !text-[11px]"
            >
              <Mail size={13} /> {lang === 'fr' ? 'Courriel' : 'Email'}
            </button>
            <button
              data-testid="share-facebook-btn"
              onClick={shareFacebook}
              className="btn-outline gap-1 !min-h-[40px] !text-[11px]"
            >
              <Facebook size={13} /> Facebook
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
